import { create } from 'zustand';
import type { NotificationMessage, WSMessage, WSEventType } from '@/types';

interface AppState {
  // Sidebar
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  // Notifications
  notifications: NotificationMessage[];
  unreadCount: number;
  addNotification: (notification: NotificationMessage) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  clearNotifications: () => void;

  // WebSocket
  wsConnected: boolean;
  wsMessages: WSMessage[];
  setWsConnected: (connected: boolean) => void;
  addWsMessage: (message: WSMessage) => void;

  // Theme
  theme: 'dark' | 'light';
  toggleTheme: () => void;

  // Selected account filter
  selectedAccountId: string | null;
  setSelectedAccountId: (id: string | null) => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  // Sidebar
  sidebarOpen: true,
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),

  // Notifications
  notifications: [],
  unreadCount: 0,
  addNotification: (notification: NotificationMessage) =>
    set((state) => {
      const notifications = [notification, ...state.notifications].slice(0, 100);
      const unreadCount = notifications.filter((n) => !n.is_read).length;
      return { notifications, unreadCount };
    }),
  markRead: (id: string) =>
    set((state) => {
      const notifications = state.notifications.map((n) =>
        n.id === id ? { ...n, is_read: true } : n,
      );
      const unreadCount = notifications.filter((n) => !n.is_read).length;
      return { notifications, unreadCount };
    }),
  markAllRead: () =>
    set((state) => {
      const notifications = state.notifications.map((n) => ({ ...n, is_read: true }));
      return { notifications, unreadCount: 0 };
    }),
  clearNotifications: () => set({ notifications: [], unreadCount: 0 }),

  // WebSocket
  wsConnected: false,
  wsMessages: [],
  setWsConnected: (connected: boolean) => set({ wsConnected: connected }),
  addWsMessage: (message: WSMessage) =>
    set((state) => ({
      wsMessages: [message, ...state.wsMessages].slice(0, 200),
    })),

  // Theme
  theme: 'dark',
  toggleTheme: () =>
    set((state) => {
      const theme = state.theme === 'dark' ? 'light' : 'dark';
      document.documentElement.classList.toggle('dark', theme === 'dark');
      return { theme };
    }),

  // Selected account filter
  selectedAccountId: null,
  setSelectedAccountId: (id: string | null) => set({ selectedAccountId: id }),
}));
